import { Box } from '@mui/material'
import { Coupon } from '@dtx-company/true-common/src/types/Coupon'
import {
  Plan,
  PlanExtraDetail,
  PlanExtraName,
  PlanExtraRender,
  PlanPurchaseExperienceMode
} from '@dtx-company/true-common/src/types/Plan'
import {
  PlanCostSummary,
  PlanCostSummaryType
} from '@dtx-company/inter-app/src/types/PlanCostSummary'
import { PlanExtrasForm } from '../../../ProFlex/AddOns'
import { PlanPurchaseExperienceSummaryLine } from './PlanPurchaseExperienceSummaryLine'
import { PlanPurchaseExperienceSummaryLineCoupon } from './PlanPurchaseExperienceSummaryLineCoupon'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { _Container, _Divider, _SubHeadingText } from '../PlanPurchaseExperience.styles'
import { _SummaryLineParagraph } from './PPESummarySection.styles'
import { promoAppliesToSelectedPlan } from '../../utils/promoAppliesToSelectedPlan'

export interface SummaryTopSectionProps {
  selectedPlan: Plan
  mode: PlanPurchaseExperienceMode | undefined
  promo: Partial<Coupon>
  planCostSummary: PlanCostSummary | undefined
  currentPlanExtraDetails?: PlanExtraDetail[]
  planExtraFormValues: PlanExtrasForm
  planExtraRender?: PlanExtraRender[]
}

export function PPESummaryTopSection(props: SummaryTopSectionProps): JSX.Element | null {
  const summary = props.planCostSummary

  if (props.mode === PlanPurchaseExperienceMode.CHANGE_PAYMENT) return null

  const requireChanges =
    props.planCostSummary?.type === PlanCostSummaryType.SAME &&
    props.mode === PlanPurchaseExperienceMode.PURCHASE_ADDONS
  if (requireChanges) {
    return (
      <>
        <_SubHeadingText component="h2" variant="header/subheader">
          Order Summary
        </_SubHeadingText>
        <_Container sx={{ mt: '16px' }}>
          <_SummaryLineParagraph style={{ marginTop: '4px' }}>
            <Text component="span" variant="body/small">
              Please make changes to your add-ons to see your new summary reflected here.
            </Text>
          </_SummaryLineParagraph>
        </_Container>
      </>
    )
  }

  return (
    <>
      <_SubHeadingText component="h2" variant="header/subheader">
        Order Summary
      </_SubHeadingText>
      <_Container sx={{ mt: '16px' }}>
        <PlanPurchaseExperienceSummaryLine
          appearance="primary"
          label="Base Price"
          priceInCents={summary?.basePriceInCents || 0}
        />
        {summary?.basePlanExtras?.map(extra => {
          return (
            <PlanPurchaseExperienceSummaryLine
              key={extra.planExtraId}
              appearance="primary"
              label={`${extra.quantity} ${PlanExtraName[extra.planExtraType]} included`}
              priceInCents={extra.priceInCents || 'FREE'}
            />
          )
        })}
        {summary?.additionalPlanExtras?.map(extra => {
          return (
            <PlanPurchaseExperienceSummaryLine
              key={extra.planExtraId}
              appearance="primary"
              label={`${extra.quantity} additional ${PlanExtraName[extra.planExtraType]}`}
              priceInCents={extra.priceInCents || 'FREE'}
            />
          )
        })}
        {(summary?.annualDiscountInCents ||
          summary?.prorationAppliedInCents ||
          summary?.couponDiscountInCents) && (
          <>
            <PlanPurchaseExperienceSummaryLine
              appearance="primary"
              label="Adjustments"
              priceInCents={''}
              sx={{ mt: '16px' }}
            />
            <Box sx={{ pl: '16px' }}>
              <PlanPurchaseExperienceSummaryLine
                appearance="secondary"
                label="Annual Discount"
                priceInCents={summary?.annualDiscountInCents || 0}
              />
              <PlanPurchaseExperienceSummaryLine
                appearance="secondary"
                label="Price adjustment from old plan to new"
                priceInCents={summary?.prorationAppliedInCents || 0}
              />
              <PlanPurchaseExperienceSummaryLineCoupon
                appearance="secondary"
                promo={
                  promoAppliesToSelectedPlan(props.promo, props.selectedPlan)
                    ? props.promo
                    : undefined
                }
                priceInCents={summary?.couponDiscountInCents}
              />
            </Box>
          </>
        )}
      </_Container>
      <_Divider style={{ marginTop: '24px', marginBottom: '24px' }} />
      <_Container>
        <PlanPurchaseExperienceSummaryLine
          appearance="primary"
          label="Subtotal"
          priceInCents={summary?.subtotalInCents || 0}
        />
        {/* 
          Commenting this out because won't show the tax in the summary section anymore
          Not removing it because we might wanna display it in a another step later 
          See. https://app.shortcut.com/flowcode/story/84517

          <PlanPurchaseExperienceSummaryLineTax
            appearance="secondary"
            priceInCents={summary?.taxInCents}
            sx={{ mt: '16px' }}
          /> */}
        <PlanPurchaseExperienceSummaryLine
          appearance="secondary"
          label="Credits applied"
          priceInCents={summary?.creditAppliedInCents || 0}
          sx={{ mt: '16px' }}
        />
      </_Container>
    </>
  )
}
